import { initSsoAPI, initSsoIbAPI, getAuthorize, getAccessToken, getUserInfo } from './BetSlipAPI';
import { setUserInfoToSession, renewTokens, isLogonHigh } from './LoginHooks';
import { setTokenToSession } from './LoginBase';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBroadcastChannel } from '../Common/hooks';
import { extendSession_IB, ticketExtendSession_IB, verifySession_IB } from './BetSlipIBAPI';
import { useTranslation } from 'react-i18next';
import { consoleLog } from '../Common/GlobalFunc';
import debounce from 'lodash/debounce';

export const isSsoLogin = async () => {
    //BAU SSO
    const initData = await initSsoIbAPI();
    const ssoWebId = initData?.DoCheckSSOSignInStatusTRResult?.find((i) => i.Key == 'sso_web_id')?.Value;
    const ssoEnabled = initData?.DoCheckSSOSignInStatusTRResult?.find((i) => i.Key == 'sso_enabled')?.Value;
    const ssoSignInLevel = initData?.DoCheckSSOSignInStatusTRResult?.find(
        (i) => i.Key == 'sso_sign_in_level'
    )?.Value;

    if (ssoWebId?.length > 0 && ssoEnabled == 'true' && ssoSignInLevel > 0) {
        return true;
    } else {
        return false;
    }
};

export const initSsoLogin = async () => {
    //BAU SSO
    const initData = await initSsoIbAPI();
    const ssoWebId = initData?.DoCheckSSOSignInStatusTRResult?.find((i) => i.Key == 'sso_web_id')?.Value;
    const ssoWebIdHashed = initData?.DoCheckSSOSignInStatusTRResult?.find(
        (i) => i.Key == 'sso_web_id_hashed'
    )?.Value;
    const ssoEnabled = initData?.DoCheckSSOSignInStatusTRResult?.find((i) => i.Key == 'sso_enabled')?.Value;
    const ssoGuid = initData?.DoCheckSSOSignInStatusTRResult?.find((i) => i.Key == 'sso_guid')?.Value;
    const ssoSignInLevel = initData?.DoCheckSSOSignInStatusTRResult?.find(
        (i) => i.Key == 'sso_sign_in_level'
    )?.Value;

    if (ssoWebId?.length > 0 && ssoEnabled == 'true' && ssoSignInLevel > 0) {
        sessionStorage.setItem('webID', ssoWebId);
        sessionStorage.setItem('sso_web_id', ssoWebId);
        sessionStorage.setItem('sso_guid', ssoGuid);
        sessionStorage.setItem('sso_web_id_hashed', ssoWebIdHashed);
        sessionStorage.setItem('sso_sign_in_level', ssoSignInLevel);
        return ssoWebId;
    } else if (window.globalConfig.IS_EWIN2) {
        return false;
    } else {
        setSsoLogout();
        return false;
    }
};

export const setSsoLogout = () => {
    sessionStorage.removeItem('sso_web_id');
    sessionStorage.removeItem('sso_web_id_hashed');
    sessionStorage.removeItem('sso_guid');
    sessionStorage.removeItem('sso_sign_in_level');
    sessionStorage.removeItem('sso_login_status');
    sessionStorage.removeItem('sso_login_error');
    sessionStorage.removeItem('customerHash');
    sessionStorage.removeItem('customerSegment');
    sessionStorage.removeItem('hWebID');
};

function hasErrorCode(message, code) {
    const re = new RegExp(`\\(${code}\\)`);
    return re.test(message);
}

const ERROR_CODES = {
    SESSION_EXPIRED: 120
};

const cacheDuration = 30; // in seconds
export const useSSOExpiryCheckOnFocus = ({ onExpired, enabled, throttleMs, refreshToken }) => {
    const [ssoLogoutPopupMsg, setSsoLogoutPopupMsg] = useState('');
    const lastCheckRef = useRef(0);
    const isVerifyCallingRef = useRef(false);
    const latestProsRef = useRef({
        onExpired,
        enabled,
        throttleMs,
        refreshToken
    });
    latestProsRef.current = { onExpired, enabled, throttleMs, refreshToken };
    useBroadcastChannel('ssoLogoutPopupMsg', setSsoLogoutPopupMsg);
    const { t } = useTranslation();
    const displayingSSOExpiryMessageRef = useRef(false);
    const lastCheckedAt = useRef(0);

    useEffect(() => {
        if (ssoLogoutPopupMsg === 'ssoLogoutPopupMsg') {
            clearInterval(window.refreshSessionIntervalId);
            window.refreshSessionIntervalId = undefined;

            // To prevent channel messages from other tabs from affecting users when they are not logged in.
            if (isLogonHigh()) {
                if (window.globalConfig.NEW_LOGOUT_REMINDER_FLOW) {
                    onExpired({ isChannelMessage: true });
                }
                if (!displayingSSOExpiryMessageRef.current) {
                    setTimeout(
                        () =>
                            window.alert(
                                t(
                                    window.globalConfig.RETAIN_BETS_IN_BET_SLIP_ON_LOGOUT
                                        ? 'NEW_LB_BS_SSO_SIGN_OUT_PASSIVE'
                                        : 'LB_BS_SSO_SIGN_OUT_PASSIVE'
                                )
                            ),
                        0
                    );
                }
            }

            displayingSSOExpiryMessageRef.current = false;
            setSsoLogoutPopupMsg('');
        }
    }, [ssoLogoutPopupMsg]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) return;

            const now = Date.now();
            if (now - lastCheckedAt.current <= cacheDuration * 1000) return;

            lastCheckedAt.current = now;
            onSsoStatusCheck('visible');
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [onSsoStatusCheck]);

    const onSsoStatusCheck = useCallback((source) => {
        const { enabled, onExpired, throttleMs, refreshToken } = latestProsRef.current;
        const verifySession = async () => {
            const resArr = await verifySession_IB();
            const result = resArr?.VerifySessionTRResult?.find((i) => i.Key == 'success')?.Value;
            const desc = resArr?.VerifySessionTRResult?.find((i) => i.Key == 'desc')?.Value;
            const isSessionExpiredError = hasErrorCode(desc, ERROR_CODES.SESSION_EXPIRED);
            if (result == 0) {
                clearInterval(window.refreshSessionIntervalId);
                window.refreshSessionIntervalId = undefined;

                onExpired({
                    isSessionExpiredError,
                    onDisplayingSSOExpiryMessage: () => {
                        displayingSSOExpiryMessageRef.current = true;
                        setTimeout(() => {
                            displayingSSOExpiryMessageRef.current = false;
                        }, 3000);
                    }
                });
            }
            if (result == 1) {
                ticketExtendSession_IB();
                // avoid calling extend session when in ES page
                // ES page will keep pooling GetOpenBets, which may have seq no. clash with extend session calls
                if (!/^\/([^/]+)\/football\/cashout\/?$/.test(window.location.pathname)) {
                    extendSession_IB();
                }
                refreshToken();
            }
        };
        const now = Date.now();

        if (!enabled || !isLogonHigh()) return;
        if (isVerifyCallingRef.current) return;
        if (now - lastCheckRef.current < throttleMs) return;

        lastCheckRef.current = now;
        isVerifyCallingRef.current = true;
        Promise.resolve()
            .then(() => verifySession())
            .finally(() => {
                isVerifyCallingRef.current = false;
            });
        consoleLog('SSO check:', source);
    }, []);
};

const ActivateUserEvents = [
    {
        name: 'click'
    },
    {
        name: 'scroll',
        option: { passive: true }
    }
];

export const useActivateUser = ({ activateUser }) => {
    const latestProsRef = useRef({
        activateUser
    });

    latestProsRef.current = {
        activateUser
    };

    useEffect(() => {
        const onActivity = (e) => {
            latestProsRef.current.activateUser();
        };
        const onDebounceActivity = debounce(onActivity, 800);

        ActivateUserEvents.forEach((eventItem) => {
            window.addEventListener(eventItem.name, onDebounceActivity, eventItem.option);
            if (eventItem.name === 'scroll') {
                document
                    .getElementById('scrollView')
                    ?.addEventListener(eventItem.name, onDebounceActivity, eventItem.option);
            }
        });

        return () => {
            ActivateUserEvents.forEach((eventItem) => {
                window.removeEventListener(eventItem.name, onDebounceActivity);
                if (eventItem.name === 'scroll') {
                    document.getElementById('scrollView')?.removeEventListener(eventItem.name, onDebounceActivity);
                }
            });
        };
    }, []);
};
